/** Left road section of the LUDOKA game board - PC skin (Yellow/QWERTY keyboard) */

import type { RoadSectionProps } from '../../constants/types';
import { renderMultiplePositions } from '../basics/utils/utils';
import { LEFT_CENTER_SQUARE_BOUNDS as CENTER_SQUARE_BOUNDS } from '../basics/constants/center-square-bounds';
import type { BoardPosition } from '../../constants/positions';

/**
 * Render order for left section (Yellow)
 * Ordered left to right for proper 3D overlap
 */
const RENDER_ORDER: BoardPosition[] = [
  'YS1',
  'YC1',
  'YF1',
  'YS2',
  'YC2',
  'YF2',
  'YS3',
  'YC3',
  'YF3',
  'YS4',
  'YC4',
  'YF4',
  'YS5',
  'YC5',
  'YF5',
  'YS6',
  'YC6',
  'YF6',
  'YFINAL'
];

const SCALE_MULTIPLIER = 1.5; // Horizontal section needs larger pawns

export default function PcLeftRoadSection({
  viewBox = '0 0 616 268',
  className = '',
  pawns = [],
  animatingPawnIds,
  kickingPawnIds,
  rollbackPawnIds,
  flippingPawnIds,
  onPawnClick,
  clickablePawnIds,
  pawnComponents
}: RoadSectionProps) {
  return (
    <svg
      viewBox={viewBox}
      className={className}
      overflow="visible"
      xmlns="http://www.w3.org/2000/svg"
      aria-label="Left road section with yellow final stretch"
    >
      {/* All PC visual content shifted +4.11 in Y to align with basics coordinate space */}
      <g transform="translate(0, 4.11)">
        {/* Top left corner - dark gray rounded corner */}
        <path
          d="M21.4,11.73h66.84v76.84H11.4V21.73c0-5.52,4.48-10,10-10Z"
          fill="#c4c4c4"
          stroke="black"
          strokeMiterlimit="10"
        />

        {/* Column 1: Blue accent top square */}
        <rect
          x="88.24"
          y="11.73"
          width="76.84"
          height="76.84"
          fill="#2468ff"
          stroke="black"
          strokeMiterlimit="10"
        />

        {/* Column 1 center path - middle row */}
        <rect x="94.58" y="94.91" width="64.17" height="64.17" rx="8.06" ry="8.06" fill="white" />

        {/* Left column center row - light gray */}
        <rect x="11.4" y="88.57" width="76.84" height="76.84" fill="#efefef" />

        {/* Column 1: Yellow gradient center row */}
        <rect
          x="88.24"
          y="88.57"
          width="76.84"
          height="76.84"
          fill="url(#pclt_paint0)"
          stroke="black"
          strokeMiterlimit="10"
        />
        {/* Column 1 center path - middle row (second instance for layering) */}
        <rect x="94.58" y="94.91" width="64.17" height="64.17" rx="8.06" ry="8.06" fill="white" />
        {/* Column 1 center path - top row */}
        <rect x="94.58" y="18.07" width="64.17" height="64.17" rx="8.06" ry="8.06" fill="white" />

        {/* Column 2: Blue accent center row */}
        <rect
          x="165.08"
          y="88.57"
          width="76.84"
          height="76.84"
          fill="#2468ff"
          stroke="black"
          strokeMiterlimit="10"
        />
        {/* Column 2 center path */}
        <rect x="171.42" y="94.91" width="64.17" height="64.17" rx="8.06" ry="8.06" fill="white" />

        {/* Column 3: Yellow gradient center row */}
        <rect
          x="241.92"
          y="88.57"
          width="76.84"
          height="76.84"
          fill="url(#pclt_paint1)"
          stroke="black"
          strokeMiterlimit="10"
        />
        {/* Column 3 center path */}
        <rect x="248.26" y="94.91" width="64.17" height="64.17" rx="8.06" ry="8.06" fill="white" />

        {/* Column 4: Blue accent center row */}
        <rect
          x="318.77"
          y="88.57"
          width="76.84"
          height="76.84"
          fill="#2468ff"
          stroke="black"
          strokeMiterlimit="10"
        />
        {/* Column 4 center path */}
        <rect x="325.1" y="94.91" width="64.17" height="64.17" rx="8.06" ry="8.06" fill="white" />

        {/* Column 5: Yellow gradient center row */}
        <rect
          x="395.61"
          y="88.57"
          width="76.84"
          height="76.84"
          fill="url(#pclt_paint2)"
          stroke="black"
          strokeMiterlimit="10"
        />

        {/* Dark ground border - base layer */}
        <path
          d="M11.44,228.96v25.58c.38,4.46,1.7,10.47,6.37,14.29,6.61,5.4,16.26,3.41,23.29,2.39,18.56-2.7,11.41,2.49,54.6,2.26,27.78-.15,27.21-2.27,52.54-2.33,22.87-.05,24.04,1.68,48.14,2.31,29.19.77,30.59-1.69,72.46-2.52,30.19-.6,29.02.67,62.11,0,30.29-.61,28.8-1.61,57.9-2.02,12.89-.18,16.82-.04,59.59,0,13.83.01,16.43,0,23.88,0l.12-27.39L11.44,228.96Z"
          fill="url(#pclt_paint3)"
        />

        {/* Gray ground overlay - top layer */}
        <path
          d="M11.44,216.54v25.58c.38,4.46,1.7,10.47,6.38,14.29,6.61,5.4,16.26,3.41,23.29,2.38,2.3-.34,4.21-.55,5.86-.66,11.7-.83,10.9,3.12,48.73,2.92,1.04,0,2.05,0,3.02-.03,4-.04,7.36-.14,10.29-.25,13.44-.54,17.79-1.69,32.71-1.98,1.97-.04,4.13-.06,6.52-.07,3.74,0,6.91.03,9.69.11,14.19.38,18.31,1.67,38.45,2.21,28.45.75,30.51-1.57,69.36-2.46,1.01-.03,2.04-.05,3.11-.07,2-.04,3.87-.08,5.62-.1,4.38-.06,8.01-.08,11.2-.06,2.2,0,4.18.02,6.05.05,10.64.15,17.65.54,39.24.11,25.49-.51,28.48-1.3,46.12-1.78,3.32-.09,7.17-.16,11.79-.23.32,0,.63,0,.94,0,8.84-.13,13.77-.08,31.81-.03,3.26,0,6.96,0,11.18.02,4.57,0,9.74,0,15.68.02,13.83,0,16.5,0,23.94,0v-27.39s-5.51-.17-5.51-.17l-37.75-1.03-66.56-1.81-56.47-1.54-64.66-1.76-39.8-1.08-190.21-5.18Z"
          fill="url(#pclt_paint4)"
        />

        {/* Column 6: White center path squares */}
        <rect x="401.94" y="94.91" width="64.17" height="64.17" rx="8.06" ry="8.06" fill="white" />

        {/* Top row squares - alternating gray */}
        <rect
          x="241.92"
          y="11.73"
          width="76.84"
          height="76.84"
          fill="#efefef"
          stroke="black"
          strokeMiterlimit="10"
        />
        <rect
          x="395.61"
          y="11.73"
          width="76.84"
          height="76.84"
          fill="#efefef"
          stroke="black"
          strokeMiterlimit="10"
        />
        <rect
          x="165.08"
          y="11.73"
          width="76.84"
          height="76.84"
          fill="#c4c4c4"
          stroke="black"
          strokeMiterlimit="10"
        />
        <rect
          x="318.77"
          y="11.73"
          width="76.84"
          height="76.84"
          fill="#c4c4c4"
          stroke="black"
          strokeMiterlimit="10"
        />

        {/* Bottom left corner */}
        <path
          d="M11.4,165.41h76.84v76.84H21.4c-5.52,0-10-4.48-10-10v-66.84h0Z"
          fill="#c4c4c4"
          stroke="black"
          strokeMiterlimit="10"
        />

        {/* Bottom row squares */}
        <rect
          x="88.24"
          y="165.41"
          width="76.84"
          height="76.84"
          fill="#efefef"
          stroke="black"
          strokeMiterlimit="10"
        />
        <rect
          x="241.92"
          y="165.41"
          width="76.84"
          height="76.84"
          fill="#efefef"
          stroke="black"
          strokeMiterlimit="10"
        />
        <rect
          x="395.61"
          y="165.41"
          width="76.84"
          height="76.84"
          fill="#efefef"
          stroke="black"
          strokeMiterlimit="10"
        />
        <rect
          x="165.08"
          y="165.41"
          width="76.84"
          height="76.84"
          fill="#c4c4c4"
          stroke="black"
          strokeMiterlimit="10"
        />
        <rect
          x="318.77"
          y="165.41"
          width="76.84"
          height="76.84"
          fill="#c4c4c4"
          stroke="black"
          strokeMiterlimit="10"
        />

        {/* Star decoration - shadow layer + white top layer */}
        <path
          d="M212.04,195.1l-6.62-11.95c-.64-1.15-2.28-1.2-2.98-.08l-7.23,11.59-13.41,2.6c-1.3.25-1.84,1.8-1,2.81l8.79,10.46-1.67,13.56c-.16,1.31,1.14,2.31,2.37,1.82l12.66-5.13,12.38,5.78c1.2.56,2.55-.37,2.46-1.69l-.97-13.63,9.32-9.99c.9-.97.43-2.54-.85-2.86l-13.26-3.29Z"
          fill="#303030"
        />
        <path
          d="M212.04,193.79l-6.62-11.95c-.64-1.15-2.28-1.2-2.98-.08l-7.23,11.59-13.41,2.6c-1.3.25-1.84,1.8-1,2.81l8.79,10.46-1.67,13.56c-.16,1.31,1.14,2.31,2.37,1.82l12.66-5.13,12.38,5.78c1.2.56,2.55-.37,2.46-1.69l-.97-13.63,9.32-9.99c.9-.97.43-2.54-.85-2.86l-13.26-3.29Z"
          fill="white"
        />

        {/* Keyboard key labels */}
        <text
          fill="#717272"
          fontFamily="Minecraft, Minecraft"
          fontSize="11.61"
          transform="translate(75.82 82.37)"
        >
          Q
        </text>
        <text
          fill="#717272"
          fontFamily="Minecraft, Minecraft"
          fontSize="11.61"
          transform="translate(75.82 159.36) scale(1.33 1)"
        >
          A
        </text>
        <text
          fill="#717272"
          fontFamily="Minecraft, Minecraft"
          fontSize="11.61"
          transform="translate(230.75 82.37)"
        >
          E
        </text>
        <text
          fill="#717272"
          fontFamily="Minecraft, Minecraft"
          fontSize="11.61"
          transform="translate(307.36 82.37)"
        >
          R
        </text>
        <text
          fill="#717272"
          fontFamily="Minecraft, Minecraft"
          fontSize="11.61"
          transform="translate(384.44 82.37)"
        >
          T
        </text>
        <text
          fill="#717272"
          fontFamily="Minecraft, Minecraft"
          fontSize="11.61"
          transform="translate(460.9 82.37)"
        >
          Y
        </text>
        <text
          fill="#717272"
          fontFamily="Minecraft, Minecraft"
          fontSize="11.61"
          transform="translate(75.82 238.74)"
        >
          Z
        </text>
        <text
          fill="#717272"
          fontFamily="Minecraft, Minecraft"
          fontSize="11.61"
          transform="translate(230.75 238.86)"
        >
          C
        </text>
        <text
          fill="#717272"
          fontFamily="Minecraft, Minecraft"
          fontSize="11.61"
          transform="translate(153.71 238.86)"
        >
          X
        </text>
        <text
          fill="#717272"
          fontFamily="Minecraft, Minecraft"
          fontSize="11.61"
          transform="translate(307.36 238.86)"
        >
          V
        </text>
        <text
          fill="#717272"
          fontFamily="Minecraft, Minecraft"
          fontSize="11.61"
          transform="translate(384.44 238.86)"
        >
          B
        </text>
        <text
          fill="#717272"
          fontFamily="Minecraft, Minecraft"
          fontSize="11.61"
          transform="translate(460.9 238.86)"
        >
          N
        </text>

        {/* Yellow gradient triangle/arrow on right */}
        <polygon
          points="587.71 126.99 472.45 242.25 473.18 11.73 587.71 126.99"
          fill="url(#pclt_paint5)"
          stroke="black"
          strokeMiterlimit="10"
        />

        <defs>
          {/* paint0: Yellow gradient for column 1 center row */}
          <linearGradient
            id="pclt_paint0"
            x1="88.24"
            y1="126.99"
            x2="165.08"
            y2="126.99"
            gradientUnits="userSpaceOnUse"
          >
            <stop stopColor="#fffa55" />
            <stop offset="0.27" stopColor="#fbf752" />
            <stop offset="0.49" stopColor="#f2ed4c" />
            <stop offset="0.68" stopColor="#e1de41" />
            <stop offset="0.86" stopColor="#cac832" />
            <stop offset="1" stopColor="#b2b222" />
          </linearGradient>

          {/* paint1: Yellow gradient for column 3 center row */}
          <linearGradient
            id="pclt_paint1"
            x1="241.92"
            y1="126.99"
            x2="318.77"
            y2="126.99"
            gradientUnits="userSpaceOnUse"
          >
            <stop stopColor="#fffa55" />
            <stop offset="0.27" stopColor="#fbf752" />
            <stop offset="0.49" stopColor="#f2ed4c" />
            <stop offset="0.68" stopColor="#e1de41" />
            <stop offset="0.86" stopColor="#cac832" />
            <stop offset="1" stopColor="#b2b222" />
          </linearGradient>

          {/* paint2: Yellow gradient for column 5 center row */}
          <linearGradient
            id="pclt_paint2"
            x1="395.61"
            y1="126.99"
            x2="472.45"
            y2="126.99"
            gradientUnits="userSpaceOnUse"
          >
            <stop stopColor="#fffa55" />
            <stop offset="0.27" stopColor="#fbf752" />
            <stop offset="0.49" stopColor="#f2ed4c" />
            <stop offset="0.68" stopColor="#e1de41" />
            <stop offset="0.86" stopColor="#cac832" />
            <stop offset="1" stopColor="#b2b222" />
          </linearGradient>

          {/* paint3: Dark gradient for ground base */}
          <linearGradient
            id="pclt_paint3"
            x1="241.44"
            y1="257.3"
            x2="241.44"
            y2="283.98"
            gradientUnits="userSpaceOnUse"
          >
            <stop stopColor="#1a1a1a" />
            <stop offset="1" stopColor="#000000" />
          </linearGradient>

          {/* paint4: Gray gradient for ground overlay */}
          <linearGradient
            id="pclt_paint4"
            x1="11.44"
            y1="238.86"
            x2="472.39"
            y2="238.86"
            gradientUnits="userSpaceOnUse"
          >
            <stop stopColor="#666666" />
            <stop offset="1" stopColor="#333333" />
          </linearGradient>

          {/* paint5: Yellow gradient for right triangle/arrow */}
          <linearGradient
            id="pclt_paint5"
            x1="472.45"
            y1="126.99"
            x2="587.71"
            y2="126.99"
            gradientUnits="userSpaceOnUse"
          >
            <stop stopColor="#fffa55" />
            <stop offset="0.27" stopColor="#fbf752" />
            <stop offset="0.49" stopColor="#f2ed4c" />
            <stop offset="0.68" stopColor="#e1de41" />
            <stop offset="0.86" stopColor="#cac832" />
            <stop offset="1" stopColor="#b2b222" />
          </linearGradient>
        </defs>
      </g>

      {/* Pawn rendering - OUTSIDE the translated group, in basics coordinate space */}
      {renderMultiplePositions(
        RENDER_ORDER,
        pawns,
        CENTER_SQUARE_BOUNDS,
        undefined,
        SCALE_MULTIPLIER,
        animatingPawnIds,
        onPawnClick,
        clickablePawnIds,
        rollbackPawnIds,
        kickingPawnIds,
        pawnComponents,
        flippingPawnIds
      )}
    </svg>
  );
}
